import React, { useEffect, useState, useMemo } from "react";
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    arrayUnion,
    Timestamp,
    orderBy
} from "firebase/firestore";
import { db } from "../../firebase";
import { THEME_COLOR } from "../../config/club";

export default function JoinerMatchRequests({ clubId, sessionId, uid, players }) {
    const [requests, setRequests] = useState([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    const playerMap = useMemo(() => {
        const map = {};
        players?.forEach(p => { map[p.id] = p.name; });
        return map;
    }, [players]);

    const getName = (id) => playerMap[id] || "Player";

    useEffect(() => {
        if (!uid || !clubId || !sessionId) return;
        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matchRequests"),
            where("players", "array-contains", uid),
            orderBy("createdAt", "desc")
        );
        return onSnapshot(q, (snap) => {
            setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [uid, clubId, sessionId]);

    const activeRequests = requests.filter(r => r.status === "pending" || r.status === "ready");
    const historyRequests = requests.filter(r => ["completed", "started", "cancelled", "declined"].includes(r.status));

    const acceptRequest = async (req) => {
        const ref = doc(db, "clubs", clubId, "sessions", sessionId, "matchRequests", req.id);
        const newAccepted = [...(req.accepted || []), uid];
        const updates = { accepted: arrayUnion(uid) };
        if (newAccepted.length === 4) updates.status = "ready";
        await updateDoc(ref, updates);
    };

    const declineRequest = async (req) => {
        const ref = doc(db, "clubs", clubId, "sessions", sessionId, "matchRequests", req.id);
        await updateDoc(ref, {
            declined: arrayUnion(uid),
            status: "declined",
            expiresAt: Timestamp.fromDate(new Date(Date.now() + 5 * 60 * 1000))
        });
    };

    if (!requests.length) return null;

    return (
        <div className="match-requests-container mb-5">
            {/* --- ACTIVE SECTION --- */}
            {activeRequests.length > 0 && (
                <div className="active-zone mb-4">
                    <div className="d-flex align-items-center gap-3 mb-3 px-1">
                        <span className="fw-black text-uppercase tracking-tighter" style={{ fontSize: '0.8rem', color: THEME_COLOR }}>Active</span>
                        <div className="flex-grow-1 theme-divider"></div>
                    </div>

                    {activeRequests.map(req => {
                        const isReady = req.status === "ready";
                        const acceptedCount = req.accepted?.length || 0;
                        const hasAccepted = req.accepted?.includes(uid);

                        if (isReady) {
                            return (
                                <div key={req.id} className="position-relative overflow-hidden rounded-4 border-0 mt-2 shadow-sm bg-body">
                                    <div className="position-absolute top-0 start-0 w-100 h-100 ready-pulse-border"></div>
                                    <div className="position-relative p-4 text-center">
                                        <div className="d-flex justify-content-center align-items-center mb-3">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '56px', height: '56px', backgroundColor: `${THEME_COLOR}15` }}>
                                                <i className="bi bi-person-gear fs-3" style={{ color: THEME_COLOR }}></i>
                                            </div>
                                        </div>
                                        <h6 className="fw-black text-uppercase mb-1" style={{ fontSize: '0.85rem', letterSpacing: '2px', color: 'var(--bs-emphasis-color)' }}>Pending Allocation</h6>
                                        <p className="text-secondary mb-3 px-3 mx-auto" style={{ fontSize: '0.75rem', maxWidth: '280px', lineHeight: '1.4' }}>
                                            Squad confirmed. Please wait while the admin queues your match and assigns a court.
                                        </p>
                                        <div className="mx-auto mt-2" style={{ width: '120px' }}>
                                            <div className="progress bg-secondary bg-opacity-10" style={{ height: '3px', borderRadius: '2px', overflow: 'hidden' }}>
                                                <div className="progress-bar progress-bar-animated premium-scan-line" style={{ backgroundColor: THEME_COLOR }}></div>
                                            </div>
                                            <div className="mt-2 fw-bold text-uppercase italic" style={{ fontSize: '0.6rem', letterSpacing: '1px', color: THEME_COLOR, opacity: 0.8 }}>Queueing match...</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={req.id} className="premium-card mb-4">
                                <div className="premium-card-inner">
                                    <div className="matchup-grid mb-3">
                                        <div className="team">{req.teamA?.map(p => <div key={p} className={`player-pill ${p === uid ? 'is-me' : ''}`}>{p === uid ? "You" : getName(p)}</div>)}</div>
                                        <div className="vs-circle"><span>VS</span></div>
                                        <div className="team">{req.teamB?.map(p => <div key={p} className={`player-pill ${p === uid ? 'is-me' : ''}`}>{p === uid ? "You" : getName(p)}</div>)}</div>
                                    </div>
                                    <div className="action-area">
                                        <div className="d-flex justify-content-between mb-2">
                                            <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Confirmation</small>
                                            <span className="fw-black" style={{ fontSize: '0.9rem', color: 'var(--bs-emphasis-color)' }}>{acceptedCount}/4</span>
                                        </div>
                                        {!hasAccepted ? (
                                            <div className="d-flex gap-2">
                                                <button className="btn-premium primary flex-grow-1" onClick={() => acceptRequest(req)}>Accept</button>
                                                <button className="btn-premium secondary" onClick={() => declineRequest(req)}>Decline</button>
                                            </div>
                                        ) : (
                                            <div className="waiting-tag"><i className="bi bi-hourglass-split me-2"></i> Waiting for others...</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* --- HISTORY SECTION --- */}
            {historyRequests.length > 0 && (
                <details className="history-details" open={isHistoryOpen} onToggle={(e) => setIsHistoryOpen(e.target.open)}>
                    <summary className="d-flex align-items-center justify-content-between p-3 rounded-4 history-summary cursor-pointer">
                        <span className="fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>Match Request History ({historyRequests.length})</span>
                        <i className={`bi bi-chevron-${isHistoryOpen ? 'up' : 'down'} text-muted`}></i>
                    </summary>
                    <div className="pt-3">
                        {historyRequests.map(req => (
                            <div key={req.id} className="history-item d-flex align-items-center justify-content-between p-3 mb-2 rounded-4 border theme-border">
                                <div className="d-flex align-items-center gap-3 overflow-hidden">
                                    <div className={`status-icon ${req.status}`}><i className={`bi ${req.status === 'started' || req.status === 'completed' ? 'bi-check-circle-fill' : 'bi-dash-circle'}`}></i></div>
                                    <div className="text-truncate">
                                        <div className="small fw-bold color-heading text-truncate">{req.teamA.map(p => getName(p)).join(', ')} vs {req.teamB.map(p => getName(p)).join(', ')}</div>
                                        <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.55rem' }}>{req.status}</small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </details>
            )}

            <style>{`
                .match-requests-container { --premium-border: var(--bs-border-color-translucent); --history-bg: var(--bs-tertiary-bg); }
                .theme-divider { height: 1px; background-color: var(--premium-border); }
                .theme-border { border-color: var(--premium-border) !important; }
                .fw-black { font-weight: 900; }
                .italic { font-style: italic; }
                
                /* Ready State Pulse */
                .ready-pulse-border { border: 2px solid ${THEME_COLOR}; opacity: 0.15; animation: border-glow 3s infinite ease-in-out; pointer-events: none; }
                @keyframes border-glow { 0% { opacity: 0.1; } 50% { opacity: 0.4; } 100% { opacity: 0.1; } }
                .premium-scan-line { position: relative; width: 100%; animation: scan-move 2s infinite ease-in-out; }
                @keyframes scan-move { 0% { transform: translateX(-100%); } 50% { transform: translateX(0%); } 100% { transform: translateX(100%); } }

                /* Card Styles */
                .premium-card { background: var(--premium-border); border-radius: 28px; padding: 1px; }
                .premium-card-inner { padding: 20px; background: var(--bs-body-bg); border-radius: 27px; }
                .matchup-grid { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 10px; }
                .player-pill { font-size: 0.75rem; font-weight: 700; padding: 6px 10px; background: var(--bs-tertiary-bg); border-radius: 10px; text-align: center; overflow: hidden; text-overflow: ellipsis; }
                .player-pill.is-me { 
    background: ${THEME_COLOR}; /* Use the full color, not just 15% opacity */
    color: #ffffff;             /* Explicitly white text for max contrast */
    border: 1px solid var(--card-bg); 
    font-weight: 900;           /* Bolder weight to improve legibility */
}

/* Optional: Add a subtle glow for better visibility in dark mode */
[data-bs-theme="dark"] .player-pill.is-me {
    box-shadow: 0 0 8px ${THEME_COLOR}40;
}
                .vs-circle { width: 30px; height: 30px; background: var(--bs-emphasis-color); color: var(--bs-body-bg); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; font-weight: 900; }
    .btn-premium {
    border-radius: 12px;
    padding: 10px 16px;
    font-weight: 800;
    font-size: 0.75rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

/* Primary (Accept) - Uses a brand-informed style */
.btn-premium.primary {
    background-color: var(--brand-color, #591094); /* Fallback to your primary brand color */
    color: #ffffff;
}

/* Secondary (Decline) - Adaptive Red */
.btn-premium.secondary {
    background-color: var(--surface-bg);
    color: #dc3545; /* Remains readable red in both light and dark */
    border-color: var(--border-color);
}

.btn-premium.secondary:hover {
    background-color: #dc3545;
    color: #ffffff;
}
                .waiting-tag { background: ${THEME_COLOR}08; color: ${THEME_COLOR}; text-align: center; padding: 10px; border-radius: 12px; font-size: 0.7rem; font-weight: 700; border: 1px dashed ${THEME_COLOR}30; }
                .history-summary { background: var(--history-bg); }
                .status-icon.started, .status-icon.completed { color: #198754; }
                .status-icon.cancelled, .status-icon.declined { color: #dc3545; }
            `}</style>
        </div>
    );
}